import { T, bi, KVKK as KVKK_TXT } from "@/lib/i18n";
import { AuthImage } from "@/components/AuthImage";

const UI = {
  tr: { target: "Hedef Program", email: "E-posta", phone: "Telefon", location: "Konum", birth: "Doğum Tarihi", nationality: "Uyruk", summary: "Kısa Özet", contact: "İletişim" },
  en: { target: "Target Program", email: "Email", phone: "Phone", location: "Location", birth: "Date of Birth", nationality: "Nationality", summary: "Summary", contact: "Contact" },
};

const EMPTY = "—";

function SideBlock({ cat, recs, lang }) {
  const label = cat.label[lang];
  return (
    <div className="mt-4">
      <div className="text-[11px] font-bold uppercase tracking-wide text-white sec-underline pb-1 mb-2">{label}</div>
      {recs.length === 0 ? (
        <div className="text-[11px] italic text-[#7f97b8]">{EMPTY}</div>
      ) : (
        recs.map((r) => (
          <div key={r.id} className="mb-1.5">
            <div className="flex justify-between gap-2 items-start">
              <span className="text-[11.5px] font-semibold text-white leading-tight">{bi(r.title, lang) || EMPTY}</span>
              {cat.type === "level" && bi(r.level, lang) ? (
                <span className="text-[10.5px] text-[#f3c9c4] whitespace-nowrap">{bi(r.level, lang)}</span>
              ) : null}
            </div>
            {bi(r.detail, lang) ? <div className="text-[10px] text-[#c8d4e6] leading-snug">{bi(r.detail, lang)}</div> : null}
          </div>
        ))
      )}
    </div>
  );
}

function MainBlock({ cat, recs, lang, columnLabels, typeFields }) {
  const fields = typeFields[cat.type];
  const heads = columnLabels[cat.type][lang];
  return (
    <div className="mb-3">
      <h3 className="text-[12px] font-bold uppercase tracking-wide text-[#1a2e4a] sec-underline pb-1 mb-1.5">
        {cat.label[lang]}
      </h3>
      <table className="cv-table">
        <thead>
          <tr>{fields.map((f) => <th key={f}>{heads[f]}</th>)}</tr>
        </thead>
        <tbody>
          {recs.length === 0 ? (
            <tr>
              <td className="cv-empty text-center" colSpan={fields.length}>{EMPTY}</td>
            </tr>
          ) : (
            recs.map((r) => (
              <tr key={r.id}>
                {fields.map((f) => {
                  const val = bi(r[f], lang);
                  return <td key={f} className={!val ? "cv-empty" : ""}>{val || EMPTY}</td>;
                })}
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
}

export const CVPreview = ({ cv, config, lang }) => {
  if (!config) return null;
  const u = UI[lang];
  const p = cv.personal || {};
  const consents = cv.consents || {};
  const kv = KVKK_TXT[lang];
  const getRecs = (key) => (cv.categories || {})[key] || [];
  const sideCats = config.categories.filter((c) => c.column === "side");
  const mainCats = config.categories.filter((c) => c.column === "main");

  const contactRows = [
    [u.email, p.email],
    [u.phone, p.phone],
    [u.location, bi(p.location, lang)],
    [u.birth, p.birthDate],
    [u.nationality, bi(p.nationality, lang)],
  ].filter(([, v]) => v);

  return (
    <div data-testid="cv-live-preview" className="bg-white shadow-lg rounded-md overflow-hidden mx-auto" style={{ maxWidth: 820 }}>
      <div className="flex">
        {/* Left navy panel */}
        <div className="bg-[#1a2e4a] text-white p-4 sm:p-5" style={{ width: "34%" }}>
          <img src="/brand/logo.png" alt="logo" className="w-14 h-14 object-contain mx-auto mb-2" />
          {p.photoUrl ? (
            <AuthImage
              src={p.photoUrl}
              alt="photo"
              className="mx-auto mb-2 object-cover border-2 border-white rounded"
              style={{ width: 78, height: 96 }}
            />
          ) : null}
          <div className="text-center font-display font-bold text-[15px] leading-tight">{p.fullName || "\u00A0"}</div>
          {bi(p.targetProgram, lang) ? (
            <div className="text-center mt-1 mb-1">
              <div className="text-[8px] uppercase tracking-widest text-[#9db4d6]">{u.target}</div>
              <div className="text-[11px] text-[#f3c9c4] font-semibold">{bi(p.targetProgram, lang)}</div>
            </div>
          ) : null}

          <div className="mt-3">
            <div className="text-[11px] font-bold uppercase tracking-wide sec-underline pb-1 mb-2">{u.contact}</div>
            {contactRows.length === 0 ? (
              <div className="text-[11px] italic text-[#7f97b8]">{EMPTY}</div>
            ) : (
              contactRows.map(([label, val]) => (
                <div key={label} className="mb-1.5">
                  <div className="text-[8px] uppercase tracking-wide text-[#9db4d6]">{label}</div>
                  <div className="text-[11px] text-[#eef2f8] break-words">{val}</div>
                </div>
              ))
            )}
          </div>

          {bi(p.summary, lang) ? (
            <div className="mt-3">
              <div className="text-[11px] font-bold uppercase tracking-wide sec-underline pb-1 mb-2">{u.summary}</div>
              <div className="text-[10.5px] text-[#dfe7f2] leading-relaxed">{bi(p.summary, lang)}</div>
            </div>
          ) : null}

          {sideCats.map((c) => (
            <SideBlock key={c.key} cat={c} recs={getRecs(c.key)} lang={lang} />
          ))}

          {(consents.formSubmission || consents.dataSharing) && (
            <div className="mt-4 pt-3 border-t border-[#2c4366]">
              {consents.formSubmission && <div className="text-[8px] text-[#c8d4e6] mb-1">✓ {kv.form}</div>}
              {consents.dataSharing && <div className="text-[8px] text-[#c8d4e6] mb-1">✓ {kv.share}</div>}
              {consents.acceptedAt && <div className="text-[7px] text-[#7f97b8]">{String(consents.acceptedAt).slice(0, 10)}</div>}
            </div>
          )}
        </div>

        {/* Right white main area */}
        <div className="p-4 sm:p-5" style={{ width: "66%" }}>
          <div className="text-[9px] tracking-[3px] text-[#c0392b] uppercase font-bold mb-2">{T[lang].academicCv}</div>
          {mainCats.map((c) => (
            <MainBlock key={c.key} cat={c} recs={getRecs(c.key)} lang={lang} columnLabels={config.columnLabels} typeFields={config.typeFields} />
          ))}
        </div>
      </div>
    </div>
  );
};
